<template>
  <div class="container-fluid">

    <!--        top title -->
    <h1 class="text-center logo-themed-header">
      <LogoSvg class="logo-icon" color="#e1bb28"></LogoSvg>
      <span class="text-my-oscar-list">MY<span class="text-oscar">OSCAR</span>LIST</span>
    </h1>

    <!--        about the list -->
    <div class="mol-center-card-width feature-section feature-section-about-the-list">
      <h4 class="feature-section-title text-my-oscar-list"><img :src="`${$imagePathBase}win_icon.svg`" alt="Logo" class="logo-image"><span class="text-oscar">ABOUT</span>THE<span class="text-oscar">LIST</span></h4>

      <div class="origin-story-embedded-video">
        <div class="caption">dramatic reenactment</div>
        <iframe
            width="100%"
            height="100%"
            src="https://www.youtube.com/embed/6VhA8Hk_m6s?si=IGdhLWtp3wF0YoKl"
            title="YouTube video player"
            frameborder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerpolicy="strict-origin-when-cross-origin"
            allowfullscreen>
        </iframe>
      </div>

      <p>
        One Friday evening many years ago, my friend and I found ourselves in a video rental store, staring at the new-releases wall, looking for a good weekend movie.
      </p>
      <p>
        In a moment of insight, it occurred to us that the rest of the shelves were filled with movies we hadn't seen; many we'd probably enjoy more than any new release.
      </p>
      <p class="insight-box">
        We'd been watching<br><em>new movies regardless of quality</em>,<br>when we should've been watching<br><em>quality movies regardless of age.</em>
      </p>
      <p>
        The first list was a printed paper packet. It included every film ever nominated for Best Picture; and for good measure we added films nominated for Best Director, as well as the four Acting categories. Each film had two checkboxes: one for each of us.
      </p>
      <p>
        The list changed the way we watched movies. It began to feel meaningful. And we made a game of completing categories: we started watching films we never would have watched before just to say "we've seen all the Best Picture nominees for 1969".
      </p>
      <p class="insight-box">
        Without any deliberate or noble intention, our checklist compelled us to watch movies exploring new ideas and unconsidered points of view.
      </p>
      <p>
        MY<b>OSCAR</b>LIST.COM was created to share this checklist. We hope you enjoy tracking your progress, sharing with friends, and challenging yourself to watch great films you might otherwise never have seen.
      </p>
    </div>

    <!--        about the data -->
    <div class="mol-center-card-width feature-section">
      <h4 class="feature-section-title text-my-oscar-list"><img :src="`${$imagePathBase}win_icon.svg`" alt="Logo" class="logo-image"><span class="text-oscar">ABOUT</span>THE<span class="text-oscar">DATA</span></h4>

      <!--        Academy Award Database -->
      <div class="data-provider">
        <img :src="`${$imagePathBase}logos/academy/OscarLogo.svg`" class="data-provider-logo">
        <p>
          All nomination data, including film names, nomination categories, nominees and winners, was collected directly from <a href="https://awardsdatabase.oscars.org/" title="Academy Awards Database" target="blank">The Official Academy Awards Database<sup><i class="fa fa-external-link"></i></sup></a>.
        </p>
      </div>

      <!--        The Movie DB -->
      <div class="data-provider">
        <img :src="`${$imagePathBase}logos/tmdb/tmdb_logo_blue_square_2.svg`" class="data-provider-logo">
        <p>
          Most film details, including genres, foreign languages, plot overviews and movie posters, come directly from <a href="https://www.themoviedb.org/" title="TMDB" target="blank">The Movie DB<sup><i class="fa fa-external-link"></i></sup></a>.
        </p>
        <p>This product uses the TMDB API but is not endorsed or certified by TMDB.</p>
      </div>

      <!--        JustWatch -->
      <div class="data-provider">
        <img :src="`${$imagePathBase}logos/tmdb/justwatch_logo.svg`" class="data-provider-logo">
        <p>
          Up-to-date streaming service catalog info comes from <a href="https://www.justwatch.com/" title="JustWatch" target="blank">JustWatch<sup><i class="fa fa-external-link"></i></sup></a>.
        </p>
      </div>

      <!--        Gracenote -->
      <div class="data-provider">
        <ColorThemeImg :src="`${$imagePathBase}logos/gracenote/gracenote-logo.svg`" :dark_theme_src="`${$imagePathBase}logos/gracenote/gracenote-logo-dark.svg`" class="data-provider-logo"/>
        <p>
          Information about TV network schedules and local theater listings comes directly from <a href="https://gracenote.com/" title="Gracenote" target="blank">Gracenote<sup><i class="fa fa-external-link"></i></sup></a>.
        </p>
      </div>

      <!--        MongoDB -->
      <div class="data-provider">
        <ColorThemeImg :src="`${$imagePathBase}logos/mongo/MongoDB_Fores-Green.svg`" :dark_theme_src="`${$imagePathBase}logos/mongo/MongoDB_Spring-Green.svg`" class="data-provider-logo"/>
        <p>
          We use <a href="https://www.mongodb.com/" title="MongoDB" target="blank">Mongo DB<sup><i class="fa fa-external-link"></i></sup></a> as our secure storage solution for all gathered film and user data.
        </p>
      </div>

      <div class="data-provider">
        <p>
          Your data is never shared with or sold to any other party.
        </p>
      </div>
    </div>


    <!--        about the developer -->
    <div class="mol-center-card-width feature-section">
      <h4 class="feature-section-title text-my-oscar-list"><img :src="`${$imagePathBase}win_icon.svg`" alt="Logo" class="logo-image"><span class="text-oscar">ABOUT</span>THE<span class="text-oscar">DEVELOPER</span></h4>

      <!--        CSS -->
      <div class="ccs-section">
        <div class="ccs-title">
          <ColorThemeImg
              :src="`${$imagePathBase}ccs/chicago-craft-software-logo-with-name.svg`"
              :dark_theme_src="`${$imagePathBase}ccs/chicago-craft-software-logo-with-name-white-text.svg`"
              alt="ccs-logo"
              class="ccs-logo-image"
          />
        </div>
        <p>
          Chicago Craft Software LLC is a small, independent developer with a unique approach.
        </p>
        <p>
          We don't see ourselves as the next Silicon Valley unicorn; our attitude is far more like that of a food truck or small brewery.
        </p>
        <p class="insight-box">
          We value simple things, done well, using our own high standards as our compass.
        </p>
        <p>
          We love the internet but we hate what it's become: a dying landscape of AI nonsense, ugly ads, click-bait, fake news and blatant invasions of privacy.
        </p>
        <p>
         We don't employ AI to write our code or create our art.
        </p>
        <p>
          We don't react to the whims of a billionaire investor or a marketing team.
        </p>
<!--
        <p>
          The things that are most important to us today were important to us yesterday, and will be important to us tomorrow.
        </p>
-->
        <p>
          We believe we can be part of the solution, leading by example both in what we create and how we create it. If you've got thoughts that support our mission, don't hesitate to <router-link to="/contact-us" class="nav-link">contact us</router-link>.
        </p>

      </div>

    </div>

  </div> <!-- container -->
</template>


<script>



import LogoSvg from "@/components/icons/LogoSvg.vue";
import ColorThemeImg from "@/components/ColorThemeImg.vue";

export default {
  name: "AboutPage",
  components: {
    ColorThemeImg,
    LogoSvg
  },
  computed: {
    streamingServiceLogoImages() {
      return [
        "tcm-logo.svg",
        "amc-logo.svg",
        "hbo-logo.svg",
        "amazon-prime-video-logo.svg",
        "apple-tv-plus-logo.svg",
        "disney-plus-logo.svg",
        "hulu-logo.svg",
        "kanopy-logo.svg",
        "max-logo.svg",
        "netflix-logo.svg",
        "paramount-plus-logo.svg",
        "peacock-logo.svg",
        //"shorts-tv-logo.svg"
      ];
    }
  },
  methods: {
  }
}

</script>


<style scoped>

.logo-themed-header {
  color: var(--mol-color-gold);
  text-align: center;
  margin-top: 10px;
  margin-bottom: 20px;
  font-size: xxx-large;

  svg {
    height: 1.125em;
    margin-right: 0.1em;
    margin-top: -0.1275em;
  }
}

.feature-section {
  color: var(--mol-color-win-icon);

  overflow: hidden;
  margin-bottom: 40px;
  padding: 2em;

  border: solid;
  border-width: 3px;
  border-color: var(--mol-color-gold);
  border-radius: 0.375rem;

  .feature-section-title {
    color: var(--mol-color-win-icon);
    text-align: center;
    margin-bottom: 1em;
  }

  .insight-box {
    font-weight: 500;
    font-size: smaller;
    text-align: center;
    border: solid;
    border-width: thin;
    border-color: color-mix(in srgb, var(--mol-color-gold), transparent 50%);
    padding: 1em;

    margin-right: auto;
    margin-left: auto;
  }

  &.feature-section-about-the-list {
    .insight-box {
      border-style: none;
      padding-top: 0;
    }
  }


  .data-provider {
    display: inline-block;

    a {
      color: inherit;
      text-decoration: inherit;
      font-style: italic;
    }
    .fa-external-link {
      margin-left: 0.2em;
    }
  }

  :deep(.data-provider-logo) {
    float: left;
    padding: 1em;
    padding-left: 0;
    max-height: 7em;
    max-width: 40%;
  }

  .data-provider:nth-child(odd) :deep(.data-provider-logo) {
    float: right;
    padding: 1em;
    padding-right: 0;
  }
}

.text-my-oscar-list {
  text-align: center;
  font-weight: 300;

  .text-oscar {
    font-weight: 600;
    padding-left: 0.05em;
    padding-right: 0.05em;
  }

  img {
    height: 1em;
    margin-right: 0.1em;
    margin-top: -0.2em;
  }
}

.ccs-section {
  color: var(--bs-secondary-color);

  .ccs-title {
    margin-top: 3em;
    margin-bottom: 0.75em;
    text-align: center;

    .ccs-logo-image {
      width: 100%;
      max-height: 1.125em;
    }
  }

  a {
    display: inline-block;
    font-weight: 500;
    text-decoration: underline;
  }

  .insight-box {
    border-style: solid none solid none;
    border-color: var(--mol-color-ccs-blue);
    color: var(--mol-color-ccs-red);
  }
}

.origin-story-embedded-video {
  /*margin: 0 auto 0 auto;*/
  float: right;

  /* hard-coded pixel size because aspect-ratio doesn't seem to work on Safari */

  /* 25px units */
  width: 225px;
  height: 400px;

  /* 20px units */
  @media (max-width: 600px) {
    width: 180px;
    height: 320px;
  }

  /* 15px units */
  @media (max-width: 500px) {
    width: 135px;
    height: 240px;
  }

  /* 10px units */
  @media (max-width: 390px) {
    width: 90px;
    height: 160px;
  }

  /*max-width: 40%;*/
  margin: 0.4em 0 0.2em 0.4em;

  .caption {
    /*width: 100%;*/
    text-align: center;
    font-size: xx-small;
    font-style: italic;
    line-height: 105%;
    margin-bottom: 0.225em;
  }

  iframe {
    /*aspect-ratio: 9 / 16;*/
    border-style: solid;
    border-width: thin;
    border-radius: 5px;
  }
}


</style>